import logger from "src/utils/logger";

const RESERVED_JWT_CLAIMS = new Set([
  "iss",
  "sub",
  "aud",
  "exp",
  "nbf",
  "iat",
  "jti",
  "scope",
]);

const pickIdentifier = (decodedToken, fallbackId) => {
  const candidates = [
    decodedToken?.client_id,
    decodedToken?.user_id,
    decodedToken?.uid,
    decodedToken?.sub,
    decodedToken?.email,
    fallbackId,
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) {
      continue;
    }

    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
      continue;
    }

    if (typeof candidate === "number" && !Number.isNaN(candidate)) {
      return String(candidate);
    }
  }

  return null;
};

const buildTraits = (decodedToken) => {
  if (!decodedToken || typeof decodedToken !== "object") {
    return {};
  }

  return Object.entries(decodedToken).reduce((accumulator, [key, value]) => {
    if (RESERVED_JWT_CLAIMS.has(key)) {
      return accumulator;
    }

    if (
      value === undefined ||
      value === null ||
      typeof value === "object" ||
      typeof value === "function"
    ) {
      return accumulator;
    }

    accumulator[key] = value;
    return accumulator;
  }, {});
};

export const identifySmartlook = (decodedToken, fallbackId) => {
  if (typeof window === "undefined") {
    return;
  }

  const smartlook = window.smartlook;

  if (typeof smartlook !== "function") {
    logger.log("Smartlook is not available on window.");
    return;
  }

  const identifier = pickIdentifier(decodedToken, fallbackId);

  if (!identifier) {
    logger.log("Smartlook identify skipped: identifier is missing.");
    return;
  }

  try {
    const traits = buildTraits(decodedToken);
    smartlook("identify", identifier, traits);
  } catch (error) {
    logger.error("Smartlook identify failed.", error);
  }
};

export const trackSmartlookEvent = (eventName, properties = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const smartlook = window.smartlook;

  if (typeof smartlook !== "function") {
    logger.log("Smartlook is not available on window.");
    return;
  }

  try {
    smartlook("track", eventName, properties);
  } catch (error) {
    logger.error("Smartlook track failed.", error);
  }
};

export const isAndroidDevice = () =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

export const buildFileReaderTrackingPayload = ({
  selectedFile,
  reader,
  status,
  page,
  surface,
  eventSource,
}) => ({
  event_source: eventSource,
  page,
  surface,
  status,
  is_android: isAndroidDevice(),
  user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  file_type: selectedFile?.type || null,
  file_size_bytes: selectedFile?.size ?? null,
  file_last_modified: selectedFile?.lastModified ?? null,
  reader_ready_state: reader?.readyState ?? null,
  reader_error_name: reader?.error?.name || null,
  result_type: typeof reader?.result,
});
